import { useState, useEffect, type CSSProperties, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Menu } from "lucide-react";

import { cn } from "../../lib/utils.js";

const HR_PANEL_PREF = "hrms.employeeForm.hrPanelOpen";
const HR_PANEL_WIDTH = 280;
/** How much room the handle needs at the right edge once the drawer is shut. The
 *  centre header reads this as `--hr-rail` and pads by it, so only that row keeps
 *  clear of the handle -- the section cards below still use the full width. */
const HR_PANEL_RAIL = 12;

interface Props {
  leftNav: ReactNode;
  rightPanel: ReactNode;
  centerHeader: ReactNode;
  children: ReactNode;
}

function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

// Fills the height its parent gives it. The 3 columns (section nav / center / HR panel)
// run the full height of the page — `centerHeader` (back button, name, status, mini
// profile) sits fixed at the top of the CENTER column only, matching its width, rather
// than spanning the whole page above the columns. Only the center content pane scrolls.
export function EmployeeFormShell({ leftNav, rightPanel, centerHeader, children }: Props) {
  const width = useBreakpoint();
  const [navOpen, setNavOpen] = useState(false);
  // Remembered, so someone who works with the panel closed keeps it closed.
  const [panelOpen, setPanelOpen] = useState(() => {
    try { return localStorage.getItem(HR_PANEL_PREF) !== "0"; } catch { return true; }
  });

  function togglePanel() {
    setPanelOpen(open => {
      const next = !open;
      try { localStorage.setItem(HR_PANEL_PREF, next ? "1" : "0"); } catch { /* private browsing */ }
      return next;
    });
  }

  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  // ≥ 1024: full 3-panel; 768–1023: left nav + center (no right panel); < 768: single column

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Mobile nav toggle */}
      {isMobile && (
        <div style={{
          flexShrink: 0, zIndex: 20, background: "var(--color-surface-0)",
          borderBottom: "1px solid var(--color-surface-200)",
          padding: "8px 16px", display: "flex", gap: 8, overflowX: "auto",
        }}>
          <button
            onClick={() => setNavOpen(v => !v)}
            style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, background: "var(--color-brand-50)", color: "var(--color-brand-700)", border: "1px solid var(--color-brand-200)", borderRadius: "var(--radius-md)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Menu size={14} /> Sections
          </button>
          {navOpen && (
            <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.4)" }} onClick={() => setNavOpen(false)}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 240, background: "var(--color-surface-0)", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                {leftNav}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0, background: "var(--color-surface-50)" }}>
        {/* Left nav — hidden on mobile (shown as overlay); fixed-width column, own scroll if ever taller than the viewport */}
        {!isMobile && (
          <div style={{
            width: isTablet ? 160 : 180, flexShrink: 0, overflowY: "auto",
            borderRight: "1px solid var(--color-surface-200)",
            background: "var(--color-surface-0)",
          }}>
            {leftNav}
          </div>
        )}

        {/* Center column: fixed header (matches this column's width only) + the one
            scrollable pane. `--hr-rail` is a runtime width the header pads by, so the
            drawer handle never sits on top of the header's own controls. */}
        <div style={{
          flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
          ["--hr-rail" as string]: `${panelOpen ? 0 : HR_PANEL_RAIL}px`,
        } as CSSProperties}>
          <div style={{ flexShrink: 0 }}>{centerHeader}</div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {children}
          </div>
        </div>

        {/* Right HR panel — a drawer that slides shut, handing its width back to the
            form. Hidden on mobile/tablet (the tablet layout puts it along the bottom).
            The handle sits outside the clipping box so it stays reachable when the
            drawer is closed. */}
        {!isMobile && !isTablet && (
          <div className="relative flex shrink-0">
            <button
              type="button"
              onClick={togglePanel}
              aria-expanded={panelOpen}
              aria-label={panelOpen ? "Hide the review panel" : "Show the review panel"}
              title={panelOpen ? "Hide the review panel" : "Show the review panel"}
              className={cn(
                // Anchored to the drawer's own edge and travelling with it as the width
                // animates. Level with the mini profile row (30px = half its 60px band),
                // so it reads as the handle for the panel that starts with the name.
                // The button is a generous hit area; only the chevron shows.
                "group absolute left-0 top-[30px] z-10 flex h-12 w-6 -translate-y-1/2 items-center justify-center",
                // Tailwind v4 drives these utilities through the `translate` property,
                // so that is what has to transition -- in lockstep with the panel.
                "transition-[translate] duration-300 ease-out motion-reduce:transition-none",
                // Open: straddling the divider, half over the form and half over the
                // panel. Closed: that edge is the window edge, so it pulls fully inside
                // -- into the gap the header's `--hr-rail` padding holds open for it.
                panelOpen ? "-translate-x-1/2" : "-translate-x-full",
                "rounded-l-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              )}
            >
              {/* A chevron pointing the way the drawer will move, so the control reads
                  as a toggle at rest rather than only once you hover it. */}
              <span
                className={cn(
                  "flex size-5 items-center justify-center rounded-full ring-1 transition-colors duration-200 motion-reduce:transition-none",
                  "bg-surface-0 text-surface-500 ring-surface-200",
                  "group-hover:bg-brand-500 group-hover:text-white group-hover:ring-brand-500",
                  "group-focus-visible:bg-brand-500 group-focus-visible:text-white",
                )}
                aria-hidden
              >
                {panelOpen ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
              </span>
            </button>

            <div
              className={cn(
                "flex flex-col overflow-hidden bg-surface-0 transition-[width] duration-300 ease-out motion-reduce:transition-none",
                panelOpen && "border-l border-surface-200",
              )}
              // willChange warns the browser a width animation is coming, so it can
              // prepare instead of re-laying out cold on the first frame.
              style={{ width: panelOpen ? HR_PANEL_WIDTH : 0, willChange: "width" }}
            >
              {/* Fixed width inside the clip, so the content doesn't reflow mid-slide. */}
              <div className="flex min-h-0 flex-1 flex-col" style={{ width: HR_PANEL_WIDTH }}>
                {rightPanel}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tablet: right panel as an expandable bottom drawer */}
      {isTablet && (
        <div style={{ flexShrink: 0, overflowY: "auto", maxHeight: "40vh", borderTop: "1px solid var(--color-surface-200)", background: "var(--color-surface-0)", padding: "12px 24px" }}>
          {rightPanel}
        </div>
      )}
    </div>
  );
}
